import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Fade from '@mui/material/Fade';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import LockResetRounded from '@mui/icons-material/LockResetRounded';
import { useAuth } from '../context/AuthContext';

const extractError = (err, fallback) =>
  err.response?.data?.message ||
  err.response?.data?.errors?.[0]?.message ||
  fallback;

const ForgotPassword = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { forgotPassword } = useAuth();

  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expiresInMinutes, setExpiresInMinutes] = useState(60);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await forgotPassword(email);
      setExpiresInMinutes(data.expiresInMinutes || 60);
      setSubmitted(true);
    } catch (err) {
      setError(extractError(err, 'Something went wrong. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${theme.palette.background.default} 50%, ${alpha(theme.palette.secondary.main, 0.06)} 100%)`,
      }}
    >
      <Fade in timeout={800}>
        <Card
          sx={{
            maxWidth: 440,
            width: '100%',
            backdropFilter: 'blur(20px)',
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  mx: 'auto',
                  mb: 2,
                  background: 'linear-gradient(135deg, #6C63FF 0%, #9D97FF 100%)',
                  boxShadow: '0 4px 20px rgba(108, 99, 255, 0.3)',
                }}
              >
                <LockResetRounded sx={{ fontSize: 28 }} />
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {submitted ? 'Check your email' : 'Forgot password?'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {submitted
                  ? `If an account exists for ${email}, a reset link is on its way.`
                  : "Enter your email and we'll send you a reset link."}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {!submitted ? (
              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  autoFocus
                  sx={{ mb: 3 }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ mb: 2, py: 1.5 }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Send reset link'}
                </Button>
                <Box sx={{ textAlign: 'center' }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => navigate('/login')}
                    underline="hover"
                  >
                    Back to sign in
                  </Link>
                </Box>
              </Box>
            ) : (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3, textAlign: 'center' }}>
                  The link will expire in {expiresInMinutes} minutes.
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{ mb: 2, py: 1.5 }}
                >
                  Back to sign in
                </Button>
                <Box sx={{ textAlign: 'center' }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => { setSubmitted(false); setError(''); }}
                    underline="hover"
                  >
                    Use a different email
                  </Link>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Fade>
    </Box>
  );
};

export default ForgotPassword;
